import { Users, Heart, Share2, TrendingUp, ArrowRight, Zap } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const SocialMediaPage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);

  useEffect(() => {
    const fetchIndustryImages = async () => {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'social-media')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching industry images:', error);
      } else {
        setIndustryImages(data || []);
      }
    };

    fetchIndustryImages();
  }, []);

  const industry = {
    title: "Social Media",
    icon: Users,
    gradient: "from-pink-500 to-rose-500",
    description: "Generate engaging content, profile pictures, and brand visuals that stand out in crowded feeds and drive meaningful engagement.",
    stats: "2x engagement rate"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Heart className="w-8 h-8 text-pink-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Engaging Content</h3>
          <p className="text-gray-300 text-sm">Create scroll-stopping visuals</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Share2 className="w-8 h-8 text-pink-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Viral Potential</h3>
          <p className="text-gray-300 text-sm">Generate shareable, memorable content</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-pink-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Growth Metrics</h3>
          <p className="text-gray-300 text-sm">Boost reach and engagement rates</p>
        </CardContent>
      </Card>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Generated Social Media Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industryImages.length > 0 ? (
          industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
              <div className="aspect-square relative overflow-hidden">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">{image.title}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-3 text-center text-gray-400 py-12">
            <Users className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">No social media examples available yet</p>
            <p className="text-sm text-gray-400 mt-2">Images will be added soon</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      heroContent={heroContent}
      mainContent={
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-6">Dominating Social Media Feeds</h2>
          
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              In the attention economy of social media, visual content is king. AI-generated imagery helps creators, brands, and influencers stand out in crowded feeds with unique, eye-catching visuals that drive engagement and build communities.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Social Media Challenge</h3>
            <p>
              With millions of posts published every minute, breaking through the noise requires constant creativity and fresh content. Traditional content creation methods can't keep up with the demand for unique, engaging visuals.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Powered Social Content</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
              <li><strong className="text-white">Feed Posts:</strong> Generate trending visuals for Instagram, TikTok, and Twitter</li>
              <li><strong className="text-white">Story Content:</strong> Create engaging backgrounds and elements for stories</li>
              <li><strong className="text-white">Profile Graphics:</strong> Design unique avatars and cover images</li>
              <li><strong className="text-white">Meme Creation:</strong> Generate viral-worthy visual content</li>
              <li><strong className="text-white">Brand Consistency:</strong> Maintain visual identity across platforms</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Engagement Success</h3>
            <p>
              Social media creators using AI-generated visuals report 2x higher engagement rates, 150% more shares, and 3x faster follower growth. The ability to create unique, on-trend content consistently gives creators a significant competitive advantage.
            </p>
            
            <div className="bg-pink-500/10 border border-pink-500/20 rounded-lg p-6 mt-8">
              <h4 className="text-xl font-semibold text-pink-400 mb-3 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Boost Your Social Presence
              </h4>
              <p className="text-gray-300">
                Create captivating social media content that stops the scroll, sparks conversations, and builds your online community.
              </p>
            </div>
          </div>
        </div>
      }
      imageGallery={imageGallery}
    />
  );
};

export default SocialMediaPage;
